import type { Middleware } from 'openapi-fetch';

import { env } from '@/lib/env';

/*
 * Auth + CSRF middleware (spec §3.3).
 *   - credentials: 'include' so the BFF session cookie rides along.
 *   - CSRF token header on unsafe methods only. The token is read from a
 *     non-HttpOnly cookie the BFF sets; no tokens live in JS state.
 *   - If-Match forwarding is handled per-request by callers (ETag seam, §8);
 *     this middleware only owns the always-on concerns.
 */

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';

/*
 * Identity writes its cookies under a per-environment name when its
 * SESSION_COOKIE_SUFFIX is set (`csrf_token__dev`) and reads the scoped name
 * first, then the plain name (api-core cookie_helpers). The BFF reads exactly
 * `csrf_token__<its suffix>` then `csrf_token`, so this reads the SAME two
 * names, in the same order, from VITE_SESSION_COOKIE_SUFFIX. Other
 * environments' cookies on a shared parent domain are ignored.
 */
export function readCsrfToken(
  cookieHeader: string = document.cookie,
  suffix: string = env.SESSION_COOKIE_SUFFIX,
): string | undefined {
  const names = suffix
    ? [`${CSRF_COOKIE}__${suffix}`, CSRF_COOKIE]
    : [CSRF_COOKIE];
  const found = new Map<string, string>();
  for (const part of cookieHeader.split('; ')) {
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    const name = part.slice(0, eq);
    const value = part.slice(eq + 1);
    if (value && names.includes(name) && !found.has(name)) {
      found.set(name, decodeURIComponent(value));
    }
  }
  for (const name of names) {
    const v = found.get(name);
    if (v) return v;
  }
  return undefined;
}

export const authMiddleware: Middleware = {
  onRequest({ request }) {
    if (UNSAFE_METHODS.has(request.method.toUpperCase())) {
      const token = readCsrfToken();
      if (token) request.headers.set(CSRF_HEADER, token);
    }
    return request;
  },
};
